/**
 * Website Asset Creation Component
 * Handles Blog Posts, Landing Pages, Product Pages
 */

import React, { useState } from 'react';
import { FileText, Layout, ShoppingCart, Upload, Eye, Send, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface WebsiteAssetProps {
  clientId?: number;
  planId?: number;
  projectId?: number;
  onComplete?: (contentId: number) => void;
  onCancel?: () => void;
}

interface PageType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

const pageTypes: PageType[] = [
  { id: 'blog_post', name: 'Blog Post', icon: <FileText className="w-5 h-5" />, description: 'Article or blog content', color: 'bg-blue-500' },
  { id: 'landing_page', name: 'Landing Page', icon: <Layout className="w-5 h-5" />, description: 'Marketing landing page', color: 'bg-purple-500' },
  { id: 'product_page', name: 'Product Page', icon: <ShoppingCart className="w-5 h-5" />, description: 'E-commerce product page', color: 'bg-green-500' },
];

export default function WebsiteAssetCreation({ clientId, planId, projectId, onComplete, onCancel }: WebsiteAssetProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPageType, setSelectedPageType] = useState<PageType | null>(null);
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [galleryImages, setGalleryImages] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const [seoData, setSeoData] = useState({
    pageTitle: '',
    metaDescription: '',
    urlSlug: '',
    targetKeyword: '',
    focusKeyphrase: '',
    ogTitle: '',
    ogDescription: '',
  });

  const [contentData, setContentData] = useState({
    title: '',
    subtitle: '',
    bodyContent: '',
    excerpt: '',
    author: '',
    category: '',
    tags: '',
  });

  const [productData, setProductData] = useState({
    sku: '',
    price: '',
    compareAtPrice: '',
    inventory: '',
    weight: '',
    dimensions: '',
  });

  const handleFeaturedImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFeaturedImage(e.target.files[0]);
    }
  };

  const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setGalleryImages(Array.from(e.target.files));
    }
  };

  const generateSlug = (title: string) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleSubmit = async () => {
    if (!selectedPageType) return;

    setLoading(true);

    try {
      // Step 1: Create content item
      const contentPayload = {
        type: 'website',
        content_subtype: selectedPageType.id,
        title: contentData.title,
        description: contentData.excerpt || seoData.metaDescription,
        status: 'draft',
        client_id: clientId,
        plan_id: planId,
        project_id: projectId,
        platform_config: JSON.stringify({
          page_type: selectedPageType.id,
          seo: seoData,
          content: contentData,
          product: selectedPageType.id === 'product_page' ? productData : null,
        }),
      };

      const contentRes = await axios.post(`${API_BASE_URL}/content`, contentPayload);
      const contentId = contentRes.data.id;

      // Step 2: Upload featured image
      if (featuredImage) {
        const formData = new FormData();
        formData.append('file', featuredImage);
        formData.append('content_id', contentId.toString());
        formData.append('asset_type', 'image');
        formData.append('asset_group', 'primary');

        await axios.post(`${API_BASE_URL}/assets/upload`, formData);
      }

      // Step 3: Upload gallery images
      if (galleryImages.length > 0) {
        for (const image of galleryImages) {
          const formData = new FormData();
          formData.append('file', image);
          formData.append('content_id', contentId.toString());
          formData.append('asset_type', 'image');
          formData.append('asset_group', 'supplementary');

          await axios.post(`${API_BASE_URL}/assets/upload`, formData);
        }
      }

      // Step 4: Create workflow steps
      const workflowSteps = [
        { step_order: 1, step_name: 'Page Type Selection', step_type: 'metadata', status: 'completed' },
        { step_order: 2, step_name: 'SEO Configuration', step_type: 'metadata', status: 'completed' },
        { step_order: 3, step_name: 'Content Creation', step_type: 'content', status: 'completed' },
        { step_order: 4, step_name: 'Media Upload', step_type: 'assets', status: 'completed' },
        { step_order: 5, step_name: 'Submit for Approval', step_type: 'approval', status: 'pending' },
      ];

      for (const step of workflowSteps) {
        await axios.post(`${API_BASE_URL}/content/${contentId}/workflow/steps`, step);
      }

      alert('Website page created successfully!');
      if (onComplete) onComplete(contentId);
    } catch (error) {
      console.error('Failed to create website asset:', error);
      alert('Failed to create website page');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Website Content</h1>
              <p className="text-gray-600 mt-1">Blog posts, landing pages, and product pages</p>
            </div>
            {onCancel && (
              <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {[
              { num: 1, label: 'Page Type' },
              { num: 2, label: 'SEO' },
              { num: 3, label: 'Content' },
              { num: 4, label: 'Media' },
              { num: 5, label: 'Preview' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step.num}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:inline">{step.label}</span>
                </div>
                {i < 4 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: Page Type Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Select Page Type</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {pageTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedPageType(type)}
                    className={`p-6 rounded-lg border-2 transition text-left ${
                      selectedPageType?.id === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-12 h-12 ${type.color} rounded-lg flex items-center justify-center mb-3 text-white`}>
                      {type.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900">{type.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                  </button>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedPageType}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next: SEO Settings
                </button>
              </div>
            </div>
          )}

          {/* Step 2: SEO Configuration */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold">SEO Configuration</h2>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Title (SEO) *</label>
                <input
                  type="text"
                  value={seoData.pageTitle}
                  onChange={(e) => setSeoData({ ...seoData, pageTitle: e.target.value })}
                  maxLength={60}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="50-60 characters for optimal SEO"
                />
                <p className="text-xs text-gray-500 mt-1">{seoData.pageTitle.length}/60 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description *</label>
                <textarea
                  value={seoData.metaDescription}
                  onChange={(e) => setSeoData({ ...seoData, metaDescription: e.target.value })}
                  maxLength={160}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="150-160 characters for optimal SEO"
                />
                <p className="text-xs text-gray-500 mt-1">{seoData.metaDescription.length}/160 characters</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
                  <input
                    type="text"
                    value={seoData.urlSlug}
                    onChange={(e) => setSeoData({ ...seoData, urlSlug: generateSlug(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="page-url-slug"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Keyword *</label>
                  <input
                    type="text"
                    value={seoData.targetKeyword}
                    onChange={(e) => setSeoData({ ...seoData, targetKeyword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Primary keyword"
                  />
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">Open Graph (Social Sharing)</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={seoData.ogTitle}
                    onChange={(e) => setSeoData({ ...seoData, ogTitle: e.target.value })}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="OG Title (optional)"
                  />
                  <textarea
                    value={seoData.ogDescription}
                    onChange={(e) => setSeoData({ ...seoData, ogDescription: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="OG Description (optional)"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!seoData.pageTitle || !seoData.metaDescription || !seoData.urlSlug || !seoData.targetKeyword}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Content
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Content Creation */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Page Content</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={contentData.title}
                  onChange={(e) => {
                    setContentData({ ...contentData, title: e.target.value });
                    if (!seoData.urlSlug) {
                      setSeoData({ ...seoData, urlSlug: generateSlug(e.target.value) });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Page or article title"
                />
              </div>

              {selectedPageType?.id === 'blog_post' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={contentData.subtitle}
                    onChange={(e) => setContentData({ ...contentData, subtitle: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional subtitle"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                <textarea
                  value={contentData.bodyContent}
                  onChange={(e) => setContentData({ ...contentData, bodyContent: e.target.value })}
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Write your content here... (Markdown supported)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                <textarea
                  value={contentData.excerpt}
                  onChange={(e) => setContentData({ ...contentData, excerpt: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief summary for listings and previews"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                  <input
                    type="text"
                    value={contentData.author}
                    onChange={(e) => setContentData({ ...contentData, author: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={contentData.category}
                    onChange={(e) => setContentData({ ...contentData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    value={contentData.tags}
                    onChange={(e) => setContentData({ ...contentData, tags: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
              </div>

              {selectedPageType?.id === 'product_page' && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-green-900 mb-3">Product Information</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <input
                      type="text"
                      value={productData.sku}
                      onChange={(e) => setProductData({ ...productData, sku: e.target.value })}
                      className="px-4 py-2 border border-green-200 rounded-lg"
                      placeholder="SKU"
                    />
                    <input
                      type="text"
                      value={productData.price}
                      onChange={(e) => setProductData({ ...productData, price: e.target.value })}
                      className="px-4 py-2 border border-green-200 rounded-lg"
                      placeholder="Price"
                    />
                    <input
                      type="text"
                      value={productData.inventory}
                      onChange={(e) => setProductData({ ...productData, inventory: e.target.value })}
                      className="px-4 py-2 border border-green-200 rounded-lg"
                      placeholder="Inventory"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!contentData.title || !contentData.bodyContent}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Media
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Media Upload */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Upload Media</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image *</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <label htmlFor="featured-upload" className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-700 font-medium">Upload featured image</span>
                    </label>
                    <input
                      id="featured-upload"
                      type="file"
                      onChange={handleFeaturedImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    {featuredImage && (
                      <p className="text-sm text-gray-600 mt-2">{featuredImage.name}</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gallery Images (optional)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <label htmlFor="gallery-upload" className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-700 font-medium">Upload gallery images</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Multiple images supported</p>
                    <input
                      id="gallery-upload"
                      type="file"
                      onChange={handleGalleryUpload}
                      multiple
                      accept="image/*"
                      className="hidden"
                    />
                    {galleryImages.length > 0 && (
                      <div className="mt-3 text-left space-y-1">
                        {galleryImages.map((img, i) => (
                          <p key={i} className="text-sm text-gray-600">{img.name}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(5)}
                  disabled={!featuredImage}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Preview & Submit */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Preview & Submit</h2>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm p-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{contentData.title}</h1>
                  {contentData.subtitle && (
                    <p className="text-xl text-gray-600 mb-4">{contentData.subtitle}</p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                    {contentData.author && <span>By {contentData.author}</span>}
                    {contentData.category && <span>• {contentData.category}</span>}
                  </div>

                  {featuredImage && (
                    <div className="mb-6 bg-gray-200 h-64 rounded-lg flex items-center justify-center">
                      <p className="text-gray-500">Featured Image</p>
                    </div>
                  )}

                  <div className="prose prose-lg max-w-none">
                    <p className="whitespace-pre-wrap">{contentData.bodyContent.substring(0, 500)}...</p>
                  </div>

                  {contentData.tags && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {contentData.tags.split(',').map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">SEO Preview</h3>
                <div className="bg-white p-3 rounded">
                  <p className="text-blue-600 text-sm">{seoData.pageTitle}</p>
                  <p className="text-green-700 text-xs">yoursite.com/{seoData.urlSlug}</p>
                  <p className="text-gray-600 text-sm mt-1">{seoData.metaDescription}</p>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
